import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

function useGuestCart() {
  const queryClient = useQueryClient();

  // Get guest cart from localStorage
  const { data: guestCart = [] } = useQuery({
    queryKey: ["guestCart"],
    queryFn: () => {
      const savedCart = localStorage.getItem("cartGuest");
      return savedCart ? JSON.parse(savedCart) : [];
    },
  });

  // Add to cart mutation
  const { mutate: addToCart } = useMutation({
    mutationFn: ({ productId, quantity = 1 }) => {
      // console.log("productId", productId);
      // console.log(quantity);
      const existingItem = guestCart.find(
        (item) => item.productId === productId
      );
      let updatedCart;

      if (existingItem) {
        updatedCart = guestCart.map((item) =>
          item.productId === productId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        updatedCart = [...guestCart, { productId, quantity }];
      }

      localStorage.setItem("cartGuest", JSON.stringify(updatedCart));
      return updatedCart;
    },
    onSuccess: (updatedCart) => {
      queryClient.setQueryData(["guestCart"], updatedCart);
      toast.success("Item added to cart");
    },
    onError: () => {
      toast.error("Failed to add item to cart");
    },
  });

  // Remove from cart mutation
  const { mutate: removeFromCart } = useMutation({
    mutationFn: (productId) => {
      const updatedCart = guestCart.filter(
        (item) => item.productId !== productId
      );
      localStorage.setItem("cartGuest", JSON.stringify(updatedCart));
      return updatedCart;
    },
    onSuccess: (updatedCart) => {
      queryClient.setQueryData(["guestCart"], updatedCart);
      toast.success("Item removed from cart");
    },
    onError: () => {
      toast.error("Failed to remove item from cart");
    },
  });

  // Update quantity mutation
  const { mutate: updateQuantity, isLoading: isUpdatingQuantity } = useMutation(
    {
      mutationFn: ({ productId, quantity }) => {
        if (quantity < 1) return guestCart;

        const updatedCart = guestCart.map((item) =>
          item.productId === productId ? { ...item, quantity } : item
        );
        localStorage.setItem("cartGuest", JSON.stringify(updatedCart));
        return updatedCart;
      },
      onSuccess: (updatedCart) => {
        queryClient.setQueryData(["guestCart"], updatedCart);
        toast.success("Cart updated");
      },
      onError: () => {
        toast.error("Failed to update cart");
      },
    }
  );

  // Clear cart mutation
  const { mutate: clearCart } = useMutation({
    mutationFn: () => {
      localStorage.removeItem("cartGuest");
      return [];
    },
    onSuccess: () => {
      queryClient.setQueryData(["guestCart"], []);
      toast.success("Cart cleared");
    },
    onError: () => {
      toast.error("Failed to clear cart");
    },
  });

  const isInCart = (productId) => {
    return guestCart.some((item) => item.productId === productId);
  };

  return {
    guestCart,
    addToCart,
    removeFromCart,
    updateQuantity,
    isUpdatingQuantity,
    clearCart,
    isInCart,
  };
}

export default useGuestCart;
